import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "../core/basic/Button";
import { Select } from "../fields/base/Select";
import type { Dispatch, SetStateAction } from "react";
import { type Table, type RowData } from "@tanstack/react-table";

export function RT8Pagination<TData extends RowData>({
  table,
  className,
  pageSizeOptions,
  canSetPageSize = true,
}: {
  table: Table<TData>;
  className?: string;
  pageSizeOptions?: number[];
  canSetPageSize?: boolean;
}) {
  return (
    <PaginationWidget
      className={className}
      totalCount={table.options.rowCount}
      currentPage={table.getState().pagination.pageIndex}
      setCurrentPage={table.setPageIndex}
      pageSize={table.getState().pagination.pageSize}
      setPageSize={table.setPageSize}
      canPaginateBackward={table.getCanPreviousPage()}
      canPaginateForward={table.getCanNextPage()}
      currentPageRowCount={table.getRowModel().rows.length}
      totalPages={table.getPageCount()}
      pageSizeOptions={pageSizeOptions}
      canSetPageSize={canSetPageSize}
    />
  );
}
interface Props {
  className?: string;
  totalCount?: number;
  pageSizeOptions?: number[];
  pageSize: number;
  setPageSize: (size: number) => void;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  canPaginateForward?: boolean;
  canPaginateBackward?: boolean;
  canSetPageSize?: boolean;
  currentPageRowCount?: number;
  hideResultsCount?: boolean;
  pageSizeLabel?: (size: number) => string;
}
export function PaginationWidget({
  className,
  totalCount,
  pageSizeOptions = [5, 10, 20, 25, 50],
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalPages,
  canPaginateForward = true,
  canPaginateBackward = true,
  canSetPageSize = true,
  currentPageRowCount = pageSize,
  hideResultsCount,
  pageSizeLabel = (size) => `${size} rows`,
}: Props) {
  const totalRowCountText = totalCount && isFinite(totalCount) ? ` of ${totalCount}` : "";
  const totalPagesText = totalPages && isFinite(totalPages) ? ` of ${totalPages}` : "";
  const previousPagesRowCount = currentPage * pageSize;
  const visibleResultsMax =
    totalCount && previousPagesRowCount + currentPageRowCount > totalCount ?
      totalCount
    : previousPagesRowCount + currentPageRowCount;

  return (
    <VStack className={className} gap="small" fontSize={"large"}>
      <HStack>
        <Button
          isDisabled={!canPaginateBackward || currentPage === 0}
          variant={"contentOnly"}
          onPress={() => setCurrentPage((currentPage) => currentPage - 1)}
        >
          <FaAngleLeft />
        </Button>
        <span className={css({ fontSize: "small" })}>{`Page ${currentPage + 1}${totalPagesText}`}</span>
        {canSetPageSize ?
          <Select
            aria-label={"Page size"}
            items={pageSizeOptions.map((opt) => ({
              label: pageSizeLabel(opt),
              value: opt,
            }))}
            selectedKey={pageSize}
            onSelectionChange={(value) => setPageSize(value as number)}
          />
        : null}
        <Button
          isDisabled={!canPaginateForward}
          variant={"contentOnly"}
          onPress={() => setCurrentPage((currentPage) => currentPage + 1)}
        >
          <FaAngleRight />
        </Button>
      </HStack>
      {!hideResultsCount && (
        <HStack>
          <span
            className={css({ fontSize: "small" })}
          >{`${previousPagesRowCount + 1}-${visibleResultsMax}${totalRowCountText} results`}</span>
        </HStack>
      )}
    </VStack>
  );
}
